import type { Placement } from '@floating-ui/react-dom';
import { computePosition, flip, offset, shift } from '@floating-ui/react-dom';
import { cx } from '@linaria/core';
import {
	type PropsWithChildren,
	type RefObject,
	useEffect,
	useRef,
} from 'react';

import type { Ref } from '~types/Ref';

import { PopoverWrapper } from './Popover.styles';

export const Popover = ({
	children,
	className,
	ref,
	placement = 'top',
	offset: offsetProps = 6,
	padding: paddingProps = 5,
	onShow,
}: PropsWithChildren<{
	className?: string;
	onShow?(): void;
	placement?: Placement;
	offset?: number;
	padding?: number;
}> &
	Ref<HTMLElement>) => {
	const popoverRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const target = (ref as RefObject<HTMLElement>)?.current;
		const popover = popoverRef.current;

		if (!target || !popover) {
			return;
		}

		if (!popover.showPopover) {
			target.style.display = 'none';
			popover.style.display = 'none';
			return;
		}

		const handleMouseEnter = () => {
			if (popover) {
				onShow?.();
				popover.showPopover();
				void computePosition(target, popover, {
					placement,
					strategy: 'fixed',
					middleware: [
						flip(),
						shift({ padding: paddingProps }),
						offset(offsetProps),
					],
				}).then(({ x, y }) => {
					Object.assign(popover.style, {
						left: `${x}px`,
						top: `${y}px`,
					});
				});
			}
		};
		const handleMouseLeave = () => {
			if (popover) {
				popover.hidePopover();
			}
		};

		target.addEventListener('mouseenter', handleMouseEnter);
		target.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			target.removeEventListener('mouseenter', handleMouseEnter);
			target.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [ref, onShow, placement, paddingProps, offsetProps]);

	return (
		<div
			ref={popoverRef}
			className={cx(PopoverWrapper, className)}
			popover="manual"
			role="tooltip"
			data-name="popover"
		>
			{children}
		</div>
	);
};
